export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
export const API_VERSION = 'v1';

export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/api/v1/auth/login',
  SIGNIN: '/api/v1/auth/signin',
  REGISTER: '/api/v1/auth/register',
  LOGOUT: '/api/v1/auth/logout',
  REFRESH_TOKEN: '/api/v1/auth/refresh-token',
  FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
  RESET_PASSWORD: '/api/v1/auth/reset-password',
  CHECK_TOKEN: '/api/v1/auth/check-token',
  AUTH_ROLES: '/api/v1/auth/roles', // Public endpoint for registration
  VERIFY_PASSWORD: '/api/v1/auth/verify-password',
  KIOSK_BYPASS: '/api/v1/auth/kiosk-bypass',
  OTP_SEND: '/api/v1/otp/send',
  OTP_VERIFY: '/api/v1/otp/verify',
  OTP_RESEND: '/api/v1/otp/resend',

  // Users
  USERS: '/api/v1/users',
  USER_BY_ID: (id: number) => `/api/v1/users/${id}`,
  USER_PROFILE: '/api/v1/user/profile',
  USER_UPDATE: '/api/v1/user/profile',
  USERS_ROLES: '/api/v1/users/roles',
  USER_PROJECT_ASSIGNMENTS: (id: number) => `/api/v1/users/${id}/project-assignments`,

  // Projects
  PROJECTS: '/api/v1/projects',
  PROJECTS_ACTIVE: '/api/v1/projects/active',
  PROJECT_BY_ID: (id: number) => `/api/v1/projects/${id}`,

  // Project Assignments
  PROJECT_ASSIGNMENTS_ME: '/api/v1/project-assignments/me',

  // HSE Reports
  HSE_REPORTS: '/api/v1/hse-reports',
  HSE_REPORT_BY_ID: (id: number) => `/api/v1/hse-reports/${id}`,

  // Inspections
  INSPECTIONS: '/api/v1/inspection-reports',
  INSPECTION_BY_ID: (id: number) => `/api/v1/inspection-reports/${id}`,
  INSPECTION_CATEGORIES: '/api/v1/inspection-reports/categories',
  INSPECTION_TYPES: '/api/v1/inspection-reports/types',
  INSPECTION_COUNTS_BY_PROJECT_DATE: '/api/v1/inspection-reports/counts-by-project-date',

  // JHA
  JHA_REPORTS: '/api/v1/jha-reports',
  JHA_REPORT_BY_ID: (id: number) => `/api/v1/jha-reports/${id}`,
  JHA_COUNTS_BY_PROJECT_DATE: '/api/v1/jha-reports/counts-by-project-date',
  AOV_COUNTS_BY_PROJECT_DATE: '/api/v1/jha-reports/aov-counts-by-project-date',
  AOV_HOURS_BY_PROJECT_DATE: '/api/v1/jha-reports/aov-hours-by-project-date',
  MANPOWER_COUNTS_BY_PROJECT_DATE: '/api/v1/jha-reports/manpower-counts-by-project-date',
  INCIDENT_COUNTS_BY_PROJECT_DATE: '/api/v1/jha-reports/incident-counts-by-project-date',
  CHECKLIST_COUNTS_BY_PROJECT_DATE: '/api/v1/jha-reports/checklist-counts-by-project-date',
  JHA_WEATHER: '/api/v1/jha-reports/weather',

  // JHA Tasks (sub-resource)
  JHA_TASKS: (reportId: number) => `/api/v1/jha-reports/${reportId}/tasks`,
  JHA_TASK_BY_ID: (reportId: number, taskId: number) =>
    `/api/v1/jha-reports/${reportId}/tasks/${taskId}`,
  JHA_TASKS_BATCH: (reportId: number) =>
    `/api/v1/jha-reports/${reportId}/tasks/batch`,
  JHA_TASKS_BATCH_DELETE: (reportId: number) =>
    `/api/v1/jha-reports/${reportId}/tasks/batch-delete`,

  // Toolbox Meetings
  TOOLBOX_MEETINGS: '/api/v1/toolbox-meetings',
  TOOLBOX_MEETINGS_SUPERVISOR_SIGN_OUT: (toolbox_meeting_log_id: number) => `/api/v1/toolbox-meetings/${toolbox_meeting_log_id}/supervisor-sign-out`,
  TOOLBOX_MEETING_BY_ID: (id: number) => `/api/v1/toolbox-meetings/${id}`,

  // Crews
  CREWS: '/api/v1/crews',
  CREW_BY_ID: (id: number) => `/api/v1/crews/${id}`,
  CREW_MEMBERS: (crewId: number) => `/api/v1/crews/${crewId}/members`,
  CREW_MEMBERS_SEARCH: '/api/v1/crews/members/search',
  CREWS_UPLOAD: '/api/v1/crews/upload',
  CREW_UPLOAD_MEMBERS: (crewId: number) => `/api/v1/crews/${crewId}/upload`,
  CREW_PROJECT_ASSIGNMENTS: (crewId: number) => `/api/v1/crews/${crewId}/projects`,

  // Crews V2 (project-scoped)
  CREWS_V2_PROJECTS: '/api/v1/crews-v2/projects',
  CREWS_V2_PROJECT_BY_ID: (project_id: number) => `/api/v1/crews-v2/projects/${project_id}`,
  CREWS_V2_PROJECT_CREWS: (project_id: number) => `/api/v1/crews-v2/projects/${project_id}/crews`,
  CREWS_V2_PROJECT_CREW_BY_ID: (project_id: number, crew_id: number) => `/api/v1/crews-v2/projects/${project_id}/crews/${crew_id}`,
  CREWS_V2_PROJECT_CREW_MEMBERS: (project_id: number, crew_id: number) => `/api/v1/crews-v2/projects/${project_id}/crews/${crew_id}/members`,
  CREWS_V2_PROJECT_CREW_MEMBER_BY_ID: (project_id: number, crew_id: number, member_id: number) => `/api/v1/crews-v2/projects/${project_id}/crews/${crew_id}/members/${member_id}`,

  // Vendors
  VENDORS: '/api/v1/vendors',
  VENDOR_BY_ID: (id: number) => `/api/v1/vendors/${id}`,

  // Companies
  COMPANIES: '/api/v1/companies',
  COMPANIES_SEARCH: '/api/v1/companies/search',
  COMPANIES_GET_OR_CREATE: '/api/v1/companies/get-or-create',

  // Dashboard
  DASHBOARD: '/api/v1/dashboard',
  DASHBOARD_METRICS: '/api/v1/dashboard/metrics',
  DASHBOARD_ANALYTICS: '/api/v1/dashboard/analytics',
  DASHBOARD_EHS_ANALYTICS: '/api/v1/dashboard/ehs-analytics',
  RECENT_ACTIVITY: '/api/v1/dashboard/recent-activity',

  // Approvals
  APPROVALS: '/api/v1/approvals',
  APPROVALS_STATS: '/api/v1/approvals/stats',

  // User Roles
  USER_ROLES: '/api/v1/user/roles',

  // Feedback
  FEEDBACK: '/api/v1/feedback',
  FEEDBACK_CATEGORIES: '/api/v1/feedback/categories',
};

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;
